import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { Conflict, isConflictDto } from '@src/data/types/Conflict'

interface RequestPathnameConfig extends InternalAxiosRequestConfig {
  _requestPathname?: string
}

/**
 * Decides whether a 409 error should raise the modification-conflict dialog: the body must
 * actually be shaped like a ConflictDto (locks/reservations/chat read-markers are not), and the
 * request must have been issued from the page the user is still on (a 409 for a request fired
 * from a since-navigated-away page is stale and must not surface on the new page). (Bug 67353)
 */
export const resolveConflict = (error: AxiosError, currentPathname: string): Conflict | null => {
  if (error.response?.status !== 409) return null

  const conflictData: unknown = error.response.data
  if (!isConflictDto(conflictData)) return null

  const requestPathname = (error.config as RequestPathnameConfig | undefined)?._requestPathname
  const isStale = requestPathname !== undefined && requestPathname !== currentPathname
  if (isStale) return null

  return {
    userId: conflictData.userId,
    userFullName: conflictData.userFullName,
    updatedOn: conflictData.updatedOn,
  }
}

// No need to overcomplicate default api client configurations via redux and instances
// Axios is already a singleton and can be used as is
axios.defaults.baseURL = import.meta.env.VITE_BACKOFFICE_API_URL
axios.defaults.headers['X-requestor'] = `backofficeapp:${import.meta.env.VITE_VERSION}`

// Callback for reporting conflicts (409 errors) - set by AppConfigProvider
let reportConflict: ((conflict: Conflict) => void) | null = null

export const setReportConflict = (callback: typeof reportConflict) => {
  reportConflict = callback
}

// Callback for reporting a deprecated client version - set by AppConfigProvider
let reportVersionDeprecated: (() => void) | null = null

export const setReportVersionDeprecated = (callback: typeof reportVersionDeprecated) => {
  reportVersionDeprecated = callback
}

// Stamp every request with the pathname it was issued from, so a 409 that comes back after
// the user has already navigated elsewhere can be recognized as stale and suppressed. (Bug 67353)
axios.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  (config as RequestPathnameConfig)._requestPathname = window.location.pathname
  return config
})

// Response interceptor to handle 409 Conflict errors and detect outdated client versions
axios.interceptors.response.use(
  (response) => {
    const latestVersion = response.headers?.['x-requestor-latest-version'] as string | undefined
    const currentVersion = import.meta.env.VITE_VERSION
    if (latestVersion && currentVersion && latestVersion !== currentVersion) {
      reportVersionDeprecated?.()
    }
    return response
  },
  (error: Error) => {
    if (axios.isAxiosError(error) && reportConflict) {
      const conflict = resolveConflict(error, window.location.pathname)
      if (conflict) reportConflict(conflict)
    }
    return Promise.reject(error)
  },
)

export const setApiClientAuthorization = (token: string) => {
  axios.defaults.headers.Authorization = `Bearer ${token}`
}

export const getApiClient = () => {
  return axios
}
export default axios
